import type { AdsFilteringType } from '../../types/ads';
import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type { SavedFilter } from '../../types/savedFilters';
import {
  buildAppliedAdsFilterSnapshot,
  hasMeaningfulAppliedFilters,
} from '../Ads/utils/appliedAdsFilterSnapshot';
import { getAdsSearchQueryParams, stringifyAdsSearchParams } from '../Ads/utils/adsSearchParams';
import { normalizeAdsFilteringObject } from '../Ads/utils/normalizeAdsFilteringObject';

export interface SavedFilterApplyTarget {
  pathname: string;
  search: string;
}

const SALE_STOREFRONT_PATH = '/';
const RENT_STOREFRONT_PATH = '/ads/rent';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const normalizedValue = value.trim();
  return normalizedValue || undefined;
};

const getStringArray = <T extends string>(value: unknown): T[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const normalizedValues = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean) as T[];

  return normalizedValues.length ? normalizedValues : undefined;
};

const getNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0 ? value : undefined;

const getBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const getNestedRecord = (source: Record<string, unknown>, key: string): Record<string, unknown> => {
  const value = source[key];
  return isRecord(value) ? value : {};
};

export const buildSavedFilterApplyTarget = (savedFilter: SavedFilter): SavedFilterApplyTarget => {
  if ((savedFilter as { criteriaVersion?: unknown }).criteriaVersion !== 1) {
    throw new Error('This saved filter version is not supported');
  }

  const criteriaValue = (savedFilter as { criteria?: unknown }).criteria;
  if (!isRecord(criteriaValue)) {
    throw new Error('This saved filter cannot be applied safely');
  }

  const activityKind = criteriaValue.activityKind;
  if (activityKind !== ActivityKindEnum.Sale && activityKind !== ActivityKindEnum.Rent) {
    throw new Error('This saved filter has an invalid activity type');
  }

  const location = getNestedRecord(criteriaValue, 'location');
  const creator = getNestedRecord(criteriaValue, 'creator');
  const handover = getNestedRecord(criteriaValue, 'handover');
  const normalizedFilter = normalizeAdsFilteringObject({
    activityKind,
    areaMax: getNumber(criteriaValue.areaMax),
    areaMin: getNumber(criteriaValue.areaMin),
    baths: getStringArray<AdFieldBathsEnum>(criteriaValue.baths),
    beds: getStringArray<AdFieldBedsEnum>(criteriaValue.beds),
    completionStatus: getStringArray<CompletionStatusEnum>(criteriaValue.completionStatus),
    creator: {
      id: getString(creator.id),
      kind: getString(creator.kind),
      name: getString(creator.name),
    },
    downPayment: getNumber(criteriaValue.downPayment),
    floor: getStringArray<AdFieldFloorEnum>(criteriaValue.floor),
    floorsNumFrom: getNumber(criteriaValue.floorsNumFrom),
    floorsNumTo: getNumber(criteriaValue.floorsNumTo),
    furniture: getStringArray<AdFieldFurnitureEnum>(criteriaValue.furniture),
    handover: {
      quarter: getString(handover.quarter),
      year: getString(handover.year),
    },
    isMyAgency: getBoolean(criteriaValue.isMyAgency) === true ? true : undefined,
    keywords: getString(criteriaValue.keywords),
    locationId: getStringArray(criteriaValue.locationId),
    multiHandover: getStringArray(criteriaValue.multiHandover),
    myFriends: getBoolean(criteriaValue.myFriends) === true ? true : undefined,
    occupancy: getStringArray<AdFieldOccupancyEnum>(criteriaValue.occupancy),
    postHandover: getBoolean(criteriaValue.postHandover),
    priceMax: getNumber(criteriaValue.priceMax),
    priceMin: getNumber(criteriaValue.priceMin),
    propertyKind: getStringArray<PropertyKindEnum>(criteriaValue.propertyKind),
    renovation: getStringArray<AdFieldRenovationEnum>(criteriaValue.renovation),
    rentFrequency: getString(criteriaValue.rentFrequency) as RentPeriodEnum | undefined,
    transactionType: getStringArray<TransactionTypeEnum>(criteriaValue.transactionType),
  } satisfies AdsFilteringType);
  const pathname =
    activityKind === ActivityKindEnum.Rent ? RENT_STOREFRONT_PATH : SALE_STOREFRONT_PATH;
  const preliminarySearch = stringifyAdsSearchParams({
    addressParamAddress: getString(location.addressEn),
    addressParamLat: getString(location.coordinatesLat),
    addressParamLong: getString(location.coordinatesLong),
    addressParamName: getString(location.nameEn),
    addressParamPlaceId: getString(location.placeId),
    areaMax: normalizedFilter.areaMax,
    areaMin: normalizedFilter.areaMin,
    baths: normalizedFilter.baths,
    beds: normalizedFilter.beds,
    completionStatus: normalizedFilter.completionStatus,
    'creator.id': normalizedFilter['creator.id'],
    'creator.kind': normalizedFilter['creator.kind'],
    'creator.name': normalizedFilter['creator.name'],
    downPayment: normalizedFilter.downPayment,
    floor: normalizedFilter.floor,
    floorsNumFrom: normalizedFilter.floorsNumFrom,
    floorsNumTo: normalizedFilter.floorsNumTo,
    furniture: normalizedFilter.furniture,
    'handover.quarter': normalizedFilter['handover.quarter'],
    'handover.year': normalizedFilter['handover.year'],
    isMyAgency: normalizedFilter.isMyAgency,
    keywords: normalizedFilter.keywords,
    locationId: normalizedFilter.locationId,
    multiHandover: normalizedFilter.multiHandover,
    myFriends: normalizedFilter.myFriends,
    occupancy: normalizedFilter.occupancy,
    postHandover: normalizedFilter.postHandover,
    priceMax: normalizedFilter.priceMax,
    priceMin: normalizedFilter.priceMin,
    propertyKind: normalizedFilter.propertyKind,
    renovation: normalizedFilter.renovation,
    rentFrequency: normalizedFilter.rentFrequency,
    transactionType: normalizedFilter.transactionType,
  });
  const normalizedSnapshot = buildAppliedAdsFilterSnapshot({
    pathname,
    search: `?${preliminarySearch}`,
  });

  if (!hasMeaningfulAppliedFilters(normalizedSnapshot)) {
    throw new Error('This saved filter does not contain applicable criteria');
  }

  const search = `?${stringifyAdsSearchParams({
    ...getAdsSearchQueryParams(preliminarySearch),
    addressParamAddress: normalizedSnapshot.location?.addressEn,
    addressParamLat: normalizedSnapshot.location?.coordinatesLat,
    addressParamLong: normalizedSnapshot.location?.coordinatesLong,
    addressParamName: normalizedSnapshot.location?.nameEn,
    addressParamPlaceId: normalizedSnapshot.location?.placeId,
    'creator.id': normalizedSnapshot.creator?.id,
    'creator.kind': normalizedSnapshot.creator?.kind,
    'creator.name': normalizedSnapshot.creator?.name,
    'handover.quarter': normalizedSnapshot.handover?.quarter,
    'handover.year': normalizedSnapshot.handover?.year,
  })}`;

  return { pathname, search };
};
